import { useEffect, useState } from 'react';

import { TimedRevealChatBox } from '../components/TimedRevealChatBox';
import { ScrollingList } from '../components/ScrollingList';
import { CLIENTS } from '../components/ClientProfile';
import { PEOPLE } from '../components/PersonProfile';

export default function Home() {
  const [isSingleColumn, setIsSingleColumn] = useState<boolean>(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsSingleColumn(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="home-page bg-[#101c2c] text-white font-sans">
      <header className="text-center p-8 bg-[#17273c] mb-4">
        <div className="flex flex-col md:flex-row justify-center items-center gap-4">
          <img
            alt="AI Escape Logo"
            className="max-h-32 max-w-32"
            src="https://cdn.aiescape.io/logo.svg"
          />
          <div className="text-center md:text-left">
            <h1 className="text-5xl font-bold">AI Escape Consulting</h1>
            <p className="text-2xl mt-4">Making Intelligence Less Artificial</p>
          </div>
        </div>
      </header>

      <section className="services p-4 bg-[#101c2c] mb-12">
        <div className="container mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="grid-item">
            <TimedRevealChatBox
              height={isSingleColumn ? '75vh' : '65vh'}
              location="what"
              messages={[
                {
                  type: 'text',
                  role: 'user',
                  message: 'What is AI Escape Consulting?',
                },
                {
                  type: 'text',
                  role: 'assistant',
                  message:
                    'We are a consulting firm that specializes in integrating Artificial Intelligence into business processes. We offer a variety of services including AI strategy, AI implementation, and AI training, as well as custom AI software solutions. We are dedicated to helping businesses of all sizes leverage the power of AI to drive growth and innovation.',
                  options: [
                    {
                      title: 'Tell me more',
                      subtitle: 'about your clients',
                      value: 'Tell me more about your clients',
                    },
                    {
                      title: 'How can AI Escape',
                      subtitle: 'help my business?',
                      value: 'How can AI Escape help my business?',
                    },
                  ],
                },
              ]}
            />
          </div>
          <div className="grid-item lg:mt-4">
            <h2 className="text-3xl font-bold mb-4">Our Clients</h2>
            <ScrollingList children={CLIENTS} />
          </div>
        </div>
      </section>

      <section className="services p-4 bg-[#101c2c] mb-12">
        <div className="container mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="grid-item">
            <h2 className="text-3xl font-bold mb-4">Our People</h2>
            <ScrollingList children={PEOPLE} />
          </div>
          <div className="grid-item lg:mt-4">
            <TimedRevealChatBox
              height={isSingleColumn ? '75vh' : '65vh'}
              location="who"
              messages={[
                {
                  type: 'text',
                  role: 'user',
                  message: 'Who is AI Escape Consulting?',
                },
                {
                  type: 'text',
                  role: 'assistant',
                  message:
                    'We were founded by Max Weinzierl, a Ph.D. candidate in Computer Science with over 10 years of experience in AI. We are passionate about helping businesses harness the power of AI to drive growth and innovation. Our team consists of AI experts and service specialists who are dedicated to delivering exceptional products and insights to our clients.',
                  options: [
                    {
                      title: 'Tell me more',
                      subtitle: 'about Max',
                      value: 'Tell me more about Max',
                    },
                    {
                      title: 'What experience',
                      subtitle: 'does the team have?',
                      value: 'What experience does the team have?',
                    },
                  ],
                },
              ]}
            />
          </div>
        </div>
      </section>

      <section className="services p-4 bg-[#101c2c]">
        <div className="container mx-auto items-center grid grid-cols-1 lg:max-w-3xl gap-8">
          <div>
            <h2 className="text-3xl font-bold mb-4">Our Services</h2>
            <TimedRevealChatBox
              height="80vh"
              location="how"
              messages={[
                {
                  type: 'text',
                  role: 'user',
                  message: 'How can AI Escape Consulting help?',
                },
                {
                  type: 'text',
                  role: 'assistant',
                  message:
                    'Tell us a bit about your business! Try asking a question or selecting one of the options below.',
                  options: [
                    {
                      title: 'How can AI',
                      subtitle: 'improve my operations?',
                      value: 'How can AI improve my operations?',
                    },
                    {
                      title: 'What can AI',
                      subtitle: 'tell me about my customers?',
                      value: 'What can AI tell me about my customers?',
                    },
                    {
                      title: 'Can AI predict',
                      subtitle: 'my sales?',
                      value: 'Can AI predict my sales?',
                    },
                    {
                      title: 'Is AI Escape right',
                      subtitle: 'for my business?',
                      value: 'Is AI Escape right for my business?',
                    },
                  ],
                },
              ]}
            />
          </div>
        </div>
      </section>
      <footer className="text-center p-6 bg-[#101c2c]">
        <p className="mb-4">Schedule a free consultation today:</p>
        {/* Teams meeting is offset to the right whe wrapping */}
        <div className="flex justify-center space-x-6 mb-4">
          <a
            className="flex items-center border-2 border-blue-950 rounded-lg p-2 text-blue-400 hover:text-blue-300 h-16 hover:bg-blue-950"
            href="https://clients.aiescape.io/meetings/max-weinzierl/consultation"
          >
            <svg className="mr-2 flex-shrink-0" height="30" width="30">
              <image
                height="30"
                width="30"
                xlinkHref="https://developers.zoom.us/svgs/favicon.svg"
              />
            </svg>
            <span>Zoom Meeting</span>
          </a>
          <a
            className="flex items-center border-2 border-blue-950 rounded-lg p-2 text-blue-400 hover:text-blue-300 h-16 hover:bg-blue-950"
            href="https://clients.aiescape.io/meetings/max-weinzierl/consultation-teams"
          >
            <svg className="mr-2 flex-shrink-0" height="30" width="30">
              <image
                height="30"
                width="30"
                xlinkHref="https://upload.wikimedia.org/wikipedia/commons/c/c9/Microsoft_Office_Teams_%282018%E2%80%93present%29.svg"
              />
            </svg>
            <span>Teams Meeting</span>
          </a>
        </div>
        <p className="mb-4">
          Contact us at{' '}
          <a className="text-blue-400 hover:text-blue-300" href="mailto:consulting@aiescape.io">
            consulting@aiescape.io
          </a>{' '}
          for more information.
        </p>
        <p className="text-sm">© 2025 AI Escape LLC - All Rights Reserved</p>
      </footer>
    </div>
  );
}
